export const normalizeFrequency = (frequency) => {
    const normalized = (frequency || "daily").toLowerCase();
    const supported = ["daily", "weekly", "fortnightly", "monthly", "quarterly", "6-monthly", "yearly"];
    return supported.includes(normalized) ? normalized : "daily";
};

export const getPeriodStartDate = (frequency, nowDate = new Date()) => {
    const normalized = normalizeFrequency(frequency);
    const date = new Date(nowDate);
    date.setHours(0, 0, 0, 0);

    if (normalized === "daily") return date;

    if (normalized === "weekly") {
        const day = date.getDay();
        const diffToMonday = (day + 6) % 7;
        date.setDate(date.getDate() - diffToMonday);
        return date;
    }

    if (normalized === "fortnightly") {
        const day = date.getDay();
        const diffToMonday = (day + 6) % 7;
        date.setDate(date.getDate() - diffToMonday);
        return date;
    }

    if (normalized === "monthly") {
        date.setDate(1);
        return date;
    }

    if (normalized === "quarterly") {
        const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
        date.setMonth(quarterStartMonth, 1);
        return date;
    }

    if (normalized === "6-monthly") {
        const month = date.getMonth();
        const halfYearStartMonth = month < 6 ? 0 : 6;
        date.setMonth(halfYearStartMonth, 1);
        return date;
    }

    date.setMonth(0, 1);
    return date;
};

export const getNextPeriodStartDate = (frequency, nowDate = new Date()) => {
    const normalized = normalizeFrequency(frequency);
    const start = getPeriodStartDate(normalized, nowDate);

    if (normalized === "daily") {
        start.setDate(start.getDate() + 1);
        return start;
    }

    if (normalized === "weekly") {
        start.setDate(start.getDate() + 7);
        return start;
    }

    if (normalized === "monthly") {
        start.setMonth(start.getMonth() + 1);
        return start;
    }

    if (normalized === "quarterly") {
        start.setMonth(start.getMonth() + 3);
        return start;
    }

    if (normalized === "6-monthly") {
        start.setMonth(start.getMonth() + 6);
        return start;
    }

    start.setFullYear(start.getFullYear() + 1);
    return start;
};

export const parseFirestoreDate = (value) => {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const isWithinCurrentPeriod = (completedDate, frequency, nowDate = new Date()) => {
    const completed = parseFirestoreDate(completedDate);
    if (!completed) return false;

    const start = getPeriodStartDate(frequency, nowDate);
    const nextStart = getNextPeriodStartDate(frequency, nowDate);
    return completed >= start && completed < nextStart;
};

export const isTaskCompletedThisPeriod = (taskName, frequency, completedTaskEntries) => {
    return completedTaskEntries.some(
        (entry) => entry.task === taskName && isWithinCurrentPeriod(entry.completedAt, frequency)
    );
};

export const getFrequencyLabel = (frequency) => {
    const normalized = normalizeFrequency(frequency);
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const getDoneStatusLabel = (frequency) => {
    const normalized = normalizeFrequency(frequency);
    if (normalized === "daily") return "Done today";
    if (normalized === "weekly") return "Done this week";
    if (normalized === "monthly") return "Done this month";
    if (normalized === "quarterly") return "Done this quarter";
    if (normalized === "6-monthly") return "Done this half-year";
    return "Done this year";
};

export const getDueStatusLabel = (frequency) => {
    const normalized = normalizeFrequency(frequency);
    if (normalized === "daily") return "Due today";
    if (normalized === "weekly") return "Due this week";
    if (normalized === "monthly") return "Due this month";
    if (normalized === "quarterly") return "Due this quarter";
    if (normalized === "6-monthly") return "Due this half-year";
    return "Due this year";
};

export const getPeriodContextLabel = (frequency) => {
    const normalized = normalizeFrequency(frequency);
    if (normalized === "daily") return "today";
    if (normalized === "weekly") return "this week";
    if (normalized === "monthly") return "this month";
    if (normalized === "quarterly") return "this quarter";
    if (normalized === "6-monthly") return "this half-year";
    return "this year";
};

export const getPeriodKey = (frequency, dateValue = new Date()) => {
    const normalized = normalizeFrequency(frequency);
    const start = getPeriodStartDate(normalized, dateValue);
    if (normalized === "daily") return `D-${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
    if (normalized === "weekly") return `W-${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
    if (normalized === "monthly") return `M-${start.getFullYear()}-${start.getMonth() + 1}`;
    if (normalized === "quarterly") return `Q-${start.getFullYear()}-${Math.floor(start.getMonth() / 3) + 1}`;
    if (normalized === "6-monthly") return `H-${start.getFullYear()}-${start.getMonth() < 6 ? 1 : 2}`;
    return `Y-${start.getFullYear()}`;
};